import { useEffect, useRef, useCallback, type MouseEvent } from "react";

interface Star {
  x: number;
  y: number;
  size: number;
  baseOpacity: number;
  twinkleSpeed: number;
  twinkleOffset: number;
  pulseStrength: number;
  // Burst state
  vy: number;
  streakLength: number;
}

interface ShootingStar {
  x: number;
  y: number;
  vx: number;
  vy: number;
  length: number;
  width: number;
  opacity: number;
  life: number;
  maxLife: number;
}

interface StarFieldProps {
  bursting: boolean;
}

const STAR_DENSITY = 0.0004;
const MIN_STAR_COUNT = 700;
const MAX_STAR_COUNT = 1200;
const SHOOTING_STAR_MIN_DELAY = 2800;
const SHOOTING_STAR_MAX_DELAY = 7200;

const StarField: React.FC<StarFieldProps> = ({ bursting }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<Star[]>([]);
  const shootingStarsRef = useRef<ShootingStar[]>([]);
  const burstingRef = useRef(false);
  const animFrameRef = useRef<number>(0);
  const nextShootingStarAtRef = useRef(0);

  const getStarCount = useCallback((width: number, height: number) => {
    const count = Math.round(width * height * STAR_DENSITY);
    return Math.max(MIN_STAR_COUNT, Math.min(MAX_STAR_COUNT, count));
  }, []);

  const scheduleNextShootingStar = useCallback((now: number) => {
    nextShootingStarAtRef.current =
      now +
      SHOOTING_STAR_MIN_DELAY +
      Math.random() * (SHOOTING_STAR_MAX_DELAY - SHOOTING_STAR_MIN_DELAY);
  }, []);

  const pushShootingStar = useCallback(
    (
      startX: number,
      startY: number,
      angle: number,
      speed: number,
      opacity = 0.5 + Math.random() * 0.25
    ) => {
      const maxLife = 0.55 + Math.random() * 0.18;

      shootingStarsRef.current.push({
        x: startX,
        y: startY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        length: 120 + Math.random() * 90,
        width: 1 + Math.random() * 1.4,
        opacity,
        life: 0,
        maxLife,
      });

      if (shootingStarsRef.current.length > 3) {
        shootingStarsRef.current.shift();
      }
    },
    []
  );

  const spawnAmbientShootingStar = useCallback(
    (width: number, height: number) => {
      const fromLeft = Math.random() > 0.5;
      const startX = fromLeft
        ? Math.random() * width * 0.35
        : width * (0.65 + Math.random() * 0.2);
      const startY = Math.random() * height * 0.32;
      const speed = 900 + Math.random() * 380;
      const angle = fromLeft
        ? (18 + Math.random() * 12) * (Math.PI / 180)
        : (150 + Math.random() * 12) * (Math.PI / 180);

      pushShootingStar(startX, startY, angle, speed);
    },
    [pushShootingStar]
  );

  const spawnClickShootingStar = useCallback(
    (x: number, y: number, width: number, height: number) => {
      const baseAngle = Math.random() * Math.PI * 2;
      const angle =
        Math.abs(Math.cos(baseAngle)) < 0.18
          ? baseAngle + 0.32
          : baseAngle;
      const distanceFromCenterX = Math.abs(x / width - 0.5);
      const distanceFromCenterY = Math.abs(y / height - 0.5);
      const speed =
        980 +
        Math.random() * 320 +
        (distanceFromCenterX + distanceFromCenterY) * 120;

      pushShootingStar(x, y, angle, speed, 0.72);
    },
    [pushShootingStar]
  );

  const initStars = useCallback((width: number, height: number) => {
    const stars: Star[] = [];
    for (let i = 0; i < getStarCount(width, height); i++) {
      stars.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 1.9 + 0.35,
        baseOpacity: Math.random() * 0.46 + 0.22,
        twinkleSpeed: Math.random() * 2.8 + 0.6,
        twinkleOffset: Math.random() * Math.PI * 2,
        pulseStrength: Math.random() * 0.32 + 0.16,
        vy: 0,
        streakLength: 0,
      });
    }
    starsRef.current = stars;
    shootingStarsRef.current = [];
  }, [getStarCount]);

  useEffect(() => {
    burstingRef.current = bursting;

    if (bursting) {
      // Kick stars into motion — varying speeds for depth
      starsRef.current.forEach((star) => {
        // Stars near center move faster (parallax feel)
        const canvas = canvasRef.current;
        if (!canvas) return;
        const centerX = canvas.width / 2;
        const distFromCenter = Math.abs(star.x - centerX) / (canvas.width / 2);
        // Stars closer to center streak more (in the runway)
        const speedMultiplier = 1 - distFromCenter * 0.6;
        star.vy = (Math.random() * 12 + 8) * speedMultiplier;
        star.streakLength = 0;
      });
    } else {
      // Reset stars gently
      starsRef.current.forEach((star) => {
        star.vy = 0;
        star.streakLength = 0;
      });
    }
  }, [bursting]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars(canvas.width, canvas.height);
      scheduleNextShootingStar(performance.now());
    };
    resize();
    window.addEventListener("resize", resize);

    let lastTime = performance.now();

    const draw = (time: number) => {
      const dt = Math.min((time - lastTime) / 1000, 0.05);
      lastTime = time;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (
        !burstingRef.current &&
        time >= nextShootingStarAtRef.current &&
        shootingStarsRef.current.length < 2
      ) {
        spawnAmbientShootingStar(canvas.width, canvas.height);
        scheduleNextShootingStar(time);
      }

      starsRef.current.forEach((star) => {
        if (burstingRef.current) {
          // Accelerate downward (stars streak down = viewer moving up)
          star.vy += dt * 40;
          star.y += star.vy * dt * 60;
          star.streakLength = Math.min(star.vy * 3, 80);

          // Wrap around
          if (star.y > canvas.height + 100) {
            star.y = -star.streakLength;
            star.x = Math.random() * canvas.width;
          }

          // Draw streak
          const gradient = ctx.createLinearGradient(
            star.x, star.y - star.streakLength,
            star.x, star.y
          );
          gradient.addColorStop(0, `rgba(255, 255, 255, 0)`);
          gradient.addColorStop(1, `rgba(255, 255, 255, ${Math.min(star.baseOpacity + 0.3, 1)})`);

          ctx.beginPath();
          ctx.strokeStyle = gradient;
          ctx.lineWidth = star.size * 0.8;
          ctx.moveTo(star.x, star.y - star.streakLength);
          ctx.lineTo(star.x, star.y);
          ctx.stroke();

          // Bright head
          ctx.beginPath();
          ctx.fillStyle = `rgba(255, 255, 255, ${Math.min(star.baseOpacity + 0.4, 1)})`;
          ctx.arc(star.x, star.y, star.size * 0.6, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // Gentle deceleration when burst ends
          if (star.vy > 0) {
            star.vy *= 0.92;
            star.streakLength *= 0.92;
            star.y += star.vy * dt * 60;
            if (star.vy < 0.1) {
              star.vy = 0;
              star.streakLength = 0;
            }

            if (star.y > canvas.height + 10) {
              star.y = Math.random() * canvas.height;
              star.x = Math.random() * canvas.width;
            }
          }

          // Twinkle
          const twinkle =
            Math.sin(time * 0.001 * star.twinkleSpeed + star.twinkleOffset) *
              star.pulseStrength +
            Math.sin(time * 0.0018 + star.twinkleOffset * 1.7) * 0.07;
          const opacity = star.baseOpacity + twinkle;
          const shimmerPhase =
            time * 0.00085 * star.twinkleSpeed + star.twinkleOffset * 1.3;
          const hue =
            215 +
            Math.sin(shimmerPhase) * 34 +
            Math.sin(shimmerPhase * 1.7 + 1.4) * 18;
          const starColor = `hsla(${hue}, 76%, 92%, ${Math.max(opacity, 0.05)})`;

          // Draw star dot
          ctx.beginPath();
          ctx.fillStyle = starColor;
          ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
          ctx.fill();

          // A tight iridescent shimmer instead of a big nearby glow.
          if (star.size > 1 && opacity > 0.34) {
            const shimmerGlow = ctx.createRadialGradient(
              star.x,
              star.y,
              0,
              star.x,
              star.y,
              star.size * 2.2
            );
            shimmerGlow.addColorStop(
              0,
              `hsla(${hue + 10}, 82%, 94%, ${opacity * 0.18})`
            );
            shimmerGlow.addColorStop(
              0.55,
              `hsla(${hue - 18}, 72%, 88%, ${opacity * 0.08})`
            );
            shimmerGlow.addColorStop(1, "rgba(255, 255, 255, 0)");

            ctx.beginPath();
            ctx.fillStyle = shimmerGlow;
            ctx.arc(star.x, star.y, star.size * 2.2, 0, Math.PI * 2);
            ctx.fill();
          }

          if (star.size > 1.15 && opacity > 0.46) {
            const glintAlpha = Math.min((opacity - 0.42) * 0.34, 0.22);
            const glintSize = star.size * 2.8;

            ctx.beginPath();
            ctx.strokeStyle = `hsla(${hue + 16}, 90%, 96%, ${glintAlpha})`;
            ctx.lineWidth = 0.7;
            ctx.lineCap = "round";
            ctx.moveTo(star.x - glintSize, star.y);
            ctx.lineTo(star.x + glintSize, star.y);
            ctx.moveTo(star.x, star.y - glintSize * 0.75);
            ctx.lineTo(star.x, star.y + glintSize * 0.75);
            ctx.stroke();
          }

          // Draw residual streak if decelerating
          if (star.streakLength > 0.5) {
            const gradient = ctx.createLinearGradient(
              star.x, star.y - star.streakLength,
              star.x, star.y
            );
            gradient.addColorStop(0, `rgba(255, 255, 255, 0)`);
            gradient.addColorStop(1, `rgba(255, 255, 255, ${opacity * 0.5})`);
            ctx.beginPath();
            ctx.strokeStyle = gradient;
            ctx.lineWidth = star.size * 0.6;
            ctx.moveTo(star.x, star.y - star.streakLength);
            ctx.lineTo(star.x, star.y);
            ctx.stroke();
          }
        }
      });

      if (!burstingRef.current) {
        shootingStarsRef.current = shootingStarsRef.current.filter((shootingStar) => {
          shootingStar.life += dt;
          shootingStar.x += shootingStar.vx * dt;
          shootingStar.y += shootingStar.vy * dt;

          if (shootingStar.life >= shootingStar.maxLife) {
            return false;
          }

          const lifeProgress = shootingStar.life / shootingStar.maxLife;
          const alpha =
            shootingStar.opacity *
            Math.sin(Math.min(lifeProgress, 1) * Math.PI);
          const tailX = shootingStar.x - shootingStar.vx * 0.03;
          const tailY = shootingStar.y - shootingStar.vy * 0.03;
          const dx = tailX - shootingStar.x;
          const dy = tailY - shootingStar.y;
          const distance = Math.hypot(dx, dy) || 1;
          const endX =
            shootingStar.x - (dx / distance) * shootingStar.length;
          const endY =
            shootingStar.y - (dy / distance) * shootingStar.length;

          const gradient = ctx.createLinearGradient(
            endX,
            endY,
            shootingStar.x,
            shootingStar.y
          );
          gradient.addColorStop(0, "rgba(255, 255, 255, 0)");
          gradient.addColorStop(0.7, `rgba(255, 255, 255, ${alpha * 0.45})`);
          gradient.addColorStop(1, `rgba(255, 255, 255, ${alpha})`);

          ctx.beginPath();
          ctx.strokeStyle = gradient;
          ctx.lineWidth = shootingStar.width;
          ctx.lineCap = "round";
          ctx.moveTo(endX, endY);
          ctx.lineTo(shootingStar.x, shootingStar.y);
          ctx.stroke();

          ctx.beginPath();
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
          ctx.arc(
            shootingStar.x,
            shootingStar.y,
            shootingStar.width * 1.3,
            0,
            Math.PI * 2
          );
          ctx.fill();

          return true;
        });
      } else {
        shootingStarsRef.current = [];
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [initStars, scheduleNextShootingStar, spawnAmbientShootingStar]);

  const handleCanvasClick = useCallback(
    (event: MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas || burstingRef.current) return;

      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      spawnClickShootingStar(x, y, rect.width, rect.height);
      scheduleNextShootingStar(performance.now() + 600);
    },
    [scheduleNextShootingStar, spawnClickShootingStar]
  );

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0"
      style={{ pointerEvents: "auto" }}
      onClick={handleCanvasClick}
      aria-hidden="true"
    />
  );
};

export default StarField;
